import { useEffect, useState } from "react";
import { api, fmtDate } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const ACTION_STYLE = (a) => {
  if (a.startsWith("pay.")) return "bg-indigo-100 text-indigo-800";
  if (a.startsWith("role.")) return "bg-violet-100 text-violet-800";
  if (a.startsWith("session.")) return "bg-red-100 text-red-700";
  if (a.startsWith("auth.")) return "bg-slate-100 text-slate-700";
  if (a.startsWith("sicknote.")) return "bg-amber-100 text-amber-800";
  if (a.startsWith("timeoff.")) return "bg-emerald-100 text-emerald-800";
  return "bg-sky-100 text-sky-800";
};

const CATEGORIES = ["all", "pay", "role", "profile", "timeoff", "sicknote", "session", "auth", "user"];

export default function AuditPage() {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [userFilter, setUserFilter] = useState("all");
  const [catFilter, setCatFilter] = useState("all");

  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const params = {};
    if (userFilter !== "all") params.target_user_id = userFilter;
    if (catFilter !== "all") params.action = catFilter;
    api.get("/audit", { params }).then((r) => setLogs(r.data)).catch(() => {});
  }, [userFilter, catFilter]);

  return (
    <div className="space-y-8">
      <div>
        <p className="uppercase tracking-[0.2em] text-xs font-semibold text-indigo-600 mb-2">Governance</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Audit trail</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-xl">Every change to accounts, roles, pay parameters, time off and sessions — who, what, when, old → new.</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <Select value={userFilter} onValueChange={setUserFilter}>
          <SelectTrigger data-testid="audit-user-filter" className="sm:w-56 bg-card"><SelectValue placeholder="Filter by member" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All team members</SelectItem>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger data-testid="audit-category-filter" className="sm:w-56 bg-card"><SelectValue placeholder="Filter by category" /></SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c === "all" ? "All categories" : c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-card border border-border rounded-md divide-y divide-border" data-testid="audit-list">
        {logs.map((l) => (
          <div key={l.id} className="px-4 sm:px-6 py-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className={ACTION_STYLE(l.action)}>{l.action}</Badge>
              <span className="text-sm font-medium">{l.actor_name}</span>
              {l.target_user_name && <span className="text-sm text-muted-foreground">→ {l.target_user_name}</span>}
              <span className="text-xs text-muted-foreground ml-auto">{new Date(l.timestamp).toLocaleString()}</span>
            </div>
            {l.detail && <p className="text-sm text-muted-foreground mt-1.5">{l.detail}</p>}
            {l.changes?.length > 0 && (
              <div className="mt-2 space-y-1">
                {l.changes.map((c, i) => (
                  <p key={i} className="text-xs font-mono bg-muted rounded px-2.5 py-1.5 inline-block mr-2">
                    {c.field}: <span className="text-red-600 line-through">{String(c.old ?? "—")}</span> → <span className="text-emerald-700 font-semibold">{String(c.new)}</span>
                  </p>
                ))}
              </div>
            )}
          </div>
        ))}
        {logs.length === 0 && <p className="px-6 py-10 text-sm text-muted-foreground">No audit entries match these filters.</p>}
      </div>
    </div>
  );
}
